import {
  createReplicationStore,
  ReplicationStore,
} from '@dineug/erd-editor/engine.js';
import { omit } from 'es-toolkit';

import { type AppDatabase } from '@/services/indexeddb/appDatabaseService';
import {
  addSchemaEntities,
  addSchemaEntity,
  deleteSchemaEntity,
  getSchemaEntities,
  getSchemaEntitiesWithValue,
  getSchemaEntity,
  NewSchemaEntity,
  SchemaEntity,
  SchemaEntityPatch,
  updateSchemaEntity,
} from '@/services/indexeddb/modules/schema';
import type { updateSchemaEntityAction } from '@/utils/broadcastChannel';
import { toFingerprint } from '@/utils/documentFingerprint';
import { toWidth } from '@/utils/text';

export class SchemaService {
  private cache = new Map<string, SchemaEntity & { store: ReplicationStore }>();
  private fingerprints = new Map<string, string>();
  // Its own channel rather than the bridge module, whose listener would make
  // this context decode every message the tabs exchange. Being a separate
  // instance, it also reaches the tab that runs this service in-thread.
  private channel = new BroadcastChannel('@@bridge');

  constructor(private db: AppDatabase) {}

  private createCache(entity: SchemaEntity) {
    if (this.cache.has(entity.id)) return;

    const store = createReplicationStore({ toWidth });
    this.cache.set(entity.id, { ...entity, store });
    this.load(entity.id, store, entity.value);
    store.on({
      change: ({ value }) => this.persist(entity.id, value),
    });
  }

  /**
   * Replaces the replica's value and measures later edits against it. The load
   * returns with what it derives written, which is housekeeping rather than an
   * edit.
   */
  private load(id: string, store: ReplicationStore, value: string) {
    store.setInitialValue(value);
    this.fingerprints.set(id, toFingerprint(store.value));
  }

  private persist(id: string, value: string) {
    const prev = this.cache.get(id);
    // A change that left the stored value as it was, such as a scroll on a
    // schema that saves none, has nothing to write.
    if (!prev || value === prev.value) return;

    const fingerprint = toFingerprint(value);
    const edited = fingerprint !== this.fingerprints.get(id);
    const entityValue: SchemaEntityPatch = edited
      ? { value, updateAt: Date.now() }
      : { value };

    this.fingerprints.set(id, fingerprint);
    this.cache.set(id, { ...prev, ...entityValue });
    updateSchemaEntity(this.db, id, entityValue).then(result => {
      if (!result || !edited) return;

      const action: ReturnType<typeof updateSchemaEntityAction> = {
        type: 'updateSchemaEntity',
        payload: { id, entityValue: { updateAt: entityValue.updateAt } },
      };
      this.channel.postMessage(action);
    });
  }

  async add(entityValue: NewSchemaEntity) {
    const result = await addSchemaEntity(this.db, entityValue);

    this.createCache(result);
    return result;
  }

  /** Stores imported schemas as given, leaving their replicas to the first open. */
  async import(list: NewSchemaEntity[]) {
    return await addSchemaEntities(this.db, list);
  }

  /** A new schema holding the source's latest value, the replica's when it has one. */
  async duplicate(id: string, entityValue: Pick<SchemaEntity, 'name'>) {
    const value =
      this.cache.get(id)?.store.value ??
      (await getSchemaEntity(this.db, id))?.value;
    if (value === undefined) return undefined;

    return await this.add({ ...entityValue, value });
  }

  async update(id: string, entityValue: SchemaEntityPatch) {
    const result = await updateSchemaEntity(this.db, id, entityValue);
    const prev = this.cache.get(id);

    if (prev && result) {
      this.cache.set(id, { ...prev, ...entityValue });

      if (entityValue.value) {
        this.load(id, prev.store, entityValue.value);
      }
    }

    return result;
  }

  async delete(id: string) {
    await deleteSchemaEntity(this.db, id);
    const prev = this.cache.get(id);

    if (prev) {
      this.cache.delete(id);
      this.fingerprints.delete(id);
      prev.store.destroy();
    }
  }

  /**
   * The schema to open, whose value is an open replica's runtime value, removed
   * entities included, for a second tab or a guest to seed from, so an undo sent
   * there brings an entity back whole; else the stored value, which opens one.
   */
  async get(id: string) {
    const prev = this.cache.get(id);
    if (prev) {
      return { ...omit(prev, ['store']), value: prev.store.runtimeValue };
    }

    const result = await getSchemaEntity(this.db, id);
    result && this.createCache(result);

    return result;
  }

  async getAll() {
    return await getSchemaEntities(this.db);
  }

  /** Every schema with its latest value, the replica's where one is open. */
  async getAllWithValue() {
    const list = await getSchemaEntitiesWithValue(this.db);

    return list.map(entity => {
      const cached = this.cache.get(entity.id);
      return cached ? { ...entity, value: cached.store.value } : entity;
    });
  }

  async replication(id: string, actions: any) {
    let prev = this.cache.get(id);

    if (!prev) {
      await this.get(id);
      prev = this.cache.get(id);
    }

    if (prev) {
      prev.store.dispatch(actions as any);
    }
  }
}
